import { Entity, TranslateGizmo, Vec3 } from 'playcanvas';

import { Events } from './events';
import { Scene } from './scene';
import { ToolOverlay } from './tool-overlay';

type Annotation = {
    position: [number, number, number],
    title: string,
    text: string,
    extras?: unknown,
    camera: {
        initial: {
            position: [number, number, number],
            target: [number, number, number],
            fov: number
        }
    }
};

const pack = (v: { x: number, y: number, z: number }): [number, number, number] => [v.x, v.y, v.z];

class Annotations {
    private annotations: Annotation[] = [];
    private selectedIndex = -1;
    private editingCamera = false;
    private overlay: ToolOverlay;
    private scene: Scene;
    private events: Events;
    private labels: HTMLDivElement[] = [];
    private cards: HTMLDivElement[] = [];
    private gizmo: TranslateGizmo;
    private gizmoEntity: Entity;
    private dragging = false;
    private gizmoPointerDown = false;

    constructor(scene: Scene, events: Events, parent: HTMLElement) {
        this.scene = scene;
        this.events = events;

        this.gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        this.gizmoEntity = new Entity('annotationGizmoPivot');
        scene.app.root.addChild(this.gizmoEntity);
        this.gizmo.coordSpace = events.invoke('tool.coordSpace');
        this.gizmo.on('render:update', () => {
            scene.forceRender = true;
        });
        this.gizmo.on('pointer:down', () => {
            this.gizmoPointerDown = true;
        });
        this.gizmo.on('pointer:up', () => {
            this.gizmoPointerDown = false;
        });
        this.gizmo.on('transform:start', () => {
            this.dragging = true;
        });
        this.gizmo.on('transform:move', () => {
            if (this.selectedIndex < 0) return;
            const position = this.gizmoEntity.getPosition();
            this.annotations[this.selectedIndex].position = pack(position);
            this.events.fire('annotations.changed', this.annotations);
            scene.forceRender = true;
        });
        this.gizmo.on('transform:end', () => {
            this.dragging = false;
            this.changed();
        });
        events.on('tool.coordSpace', (space: string) => {
            this.gizmo.coordSpace = space as 'local' | 'world';
            scene.forceRender = true;
        });

        this.overlay = new ToolOverlay();
        this.overlay.provider = (writer) => {
            for (const annotation of this.annotations) {
                writer.dot(new Vec3(annotation.position));
            }
        };
        scene.add(this.overlay);

        events.function('annotations', () => this.annotations);
        events.function('annotation.selected', () => this.selectedIndex);
        events.function('annotation.editingCamera', () => this.editingCamera);
        events.on('annotation.editCamera', () => {
            if (this.selectedIndex < 0) return;
            events.invoke('annotation.commitEdits');
            this.setEditingCamera(true);
        });
        events.on('annotation.cancelCamera', () => this.setEditingCamera(false));
        events.function('docDeserialize.annotations', (data: unknown) => this.deserialize(data));
        events.function('docSerialize.annotations', () => this.annotations.map(annotation => ({
            ...(annotation.extras === undefined ? {} : { extras: structuredClone(annotation.extras) }),
            position: [...annotation.position],
            title: annotation.title,
            text: annotation.text,
            camera: {
                initial: { ...annotation.camera.initial, position: [...annotation.camera.initial.position], target: [...annotation.camera.initial.target] }
            }
        })));
        events.on('annotation.update', (index: number, changes: Partial<Pick<Annotation, 'title' | 'text'>>) => {
            const annotation = this.annotations[index];
            if (!annotation) return;
            if (changes.title !== undefined) annotation.title = changes.title;
            if (changes.text !== undefined) annotation.text = changes.text;
            this.changed();
        });
        events.on('annotation.updateCamera', () => {
            if (!this.editingCamera) return;
            const annotation = this.annotations[this.selectedIndex];
            const pose = this.events.invoke('camera.getPose');
            if (!annotation || !pose) return;
            annotation.camera.initial = {
                position: pack(pose.position),
                target: pack(pose.target),
                fov: pose.fov
            };
            this.setEditingCamera(false);
            this.changed();
        });

        events.on('annotation.create', (position: Vec3) => this.create(position));
        events.on('annotation.select', (index: number) => this.select(index));
        events.on('annotation.deselect', () => this.deselect());
        events.on('annotation.delete', (index: number) => this.remove(index));
        events.on('annotation.move', (from: number, to: number) => this.move(from, to));
        events.on('annotation.deleteSelected', () => {
            if (this.selectedIndex >= 0) this.remove(this.selectedIndex);
        });
        events.on('annotation.clear', () => this.clear());
        events.on('scene.clear', () => this.clear());
        events.on('postrender', () => this.updateLabels(parent));
        const deselectOnOutsidePointer = (event: PointerEvent) => {
            if (this.gizmoPointerDown || this.dragging) return;
            const target = event.target as HTMLElement;
            // Keep selection while navigating or grabbing an annotation axis.
            // This capture listener runs before the gizmo's canvas listener,
            // so its pointer/drag flags cannot identify an axis press yet.
            if (target === scene.canvas || target.closest('#view-cube-container')) return;
            if (!target.closest('.editor-annotation-label, .annotation-list, .annotation-editor')) {
                // Commit active text fields before deselection refreshes the
                // editor panel from the last stored annotation value.
                this.events.invoke('annotation.commitEdits');
                this.deselect();
            }
        };
        // Listen on the document so clicks on toolbars, dialogs and other UI
        // also close the selected annotation.
        document.addEventListener('pointerdown', deselectOnOutsidePointer, true);
        window.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape' && this.editingCamera) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.setEditingCamera(false);
            }
        }, true);
        const exportPopup = document.getElementById('export-popup');
        if (exportPopup) {
            // The export modal lives in a separate top-level overlay and can
            // otherwise stack below the canvas annotation DOM overlay.
            new MutationObserver(() => this.updateLabels(parent)).observe(exportPopup, {
                attributes: true,
                attributeFilter: ['hidden', 'class']
            });
        }
        this.changed();
    }

    private updateLabels(parent: HTMLElement) {
        const exportPopup = document.getElementById('export-popup');
        const exportOpen = exportPopup && !exportPopup.hidden && !exportPopup.classList.contains('pcui-hidden');
        if (exportOpen) {
            this.labels.forEach((label) => {
                label.hidden = true;
            });
            this.cards.forEach((card) => {
                card.hidden = true;
            });
            return;
        }

        while (this.labels.length < this.annotations.length) {
            const index = this.labels.length;
            const label = document.createElement('div');
            label.className = 'editor-annotation-label';
            const card = document.createElement('div');
            card.className = 'editor-annotation-card';
            label.appendChild(card);
            label.addEventListener('pointerdown', (event) => {
                event.stopPropagation();
            });
            label.addEventListener('click', (event) => {
                event.stopPropagation();
                this.select(index);
            });
            parent.appendChild(label);
            this.labels.push(label);
            this.cards.push(card);
        }

        const screen = new Vec3();
        this.labels.forEach((label, index) => {
            const annotation = this.annotations[index];
            if (!annotation) {
                label.hidden = true;
                this.cards[index].hidden = true;
                return;
            }
            this.scene.camera.worldToScreen(new Vec3(annotation.position), screen);
            const cameraPosition = this.scene.camera.mainCamera.getPosition();
            const cameraForward = this.scene.camera.mainCamera.forward;
            const depth = new Vec3(annotation.position).sub(cameraPosition).dot(cameraForward);
            const visible = depth > 0 && screen.x >= 0 && screen.x <= 1 &&
                screen.y >= 0 && screen.y <= 1 && this.scene.camera.renderOverlays;
            label.hidden = !visible;
            if (visible) {
                label.textContent = annotation.title || `Annotation ${index + 1}`;
                label.appendChild(this.cards[index]);
                this.cards[index].innerHTML = '';
                const headline = document.createElement('div');
                headline.className = 'editor-annotation-card-headline';
                headline.textContent = annotation.title || `Annotation ${index + 1}`;
                const text = document.createElement('div');
                text.className = 'editor-annotation-card-text';
                text.textContent = annotation.text;
                this.cards[index].append(headline, text);
                this.cards[index].hidden = index !== this.selectedIndex;
                label.style.left = `${screen.x * this.scene.canvas.clientWidth}px`;
                label.style.top = `${screen.y * this.scene.canvas.clientHeight}px`;
                label.classList.toggle('selected', index === this.selectedIndex);
            }
        });
    }

    deserialize(data: unknown) {
        if (!Array.isArray(data)) return;
        this.setEditingCamera(false);
        this.annotations = data.filter((value): value is Annotation => {
            const annotation = value as Annotation;
            return !!annotation?.position && !!annotation?.camera?.initial;
        }).map(annotation => ({
            ...(annotation.extras === undefined ? {} : { extras: structuredClone(annotation.extras) }),
            position: [...annotation.position] as [number, number, number],
            title: annotation.title ?? '',
            text: annotation.text ?? '',
            camera: {
                initial: {
                    position: [...annotation.camera.initial.position] as [number, number, number],
                    target: [...annotation.camera.initial.target] as [number, number, number],
                    fov: annotation.camera.initial.fov ?? 60
                }
            }
        }));
        this.selectedIndex = -1;
        this.changed();
    }

    private create(position: Vec3) {
        const pose = this.events.invoke('camera.getPose');
        if (!pose) return;
        this.setEditingCamera(false);

        this.annotations.push({
            position: pack(position),
            title: `Annotation ${this.annotations.length + 1}`,
            text: '',
            camera: {
                initial: {
                    position: pack(pose.position),
                    target: pack(pose.target),
                    fov: pose.fov
                }
            }
        });
        this.selectedIndex = this.annotations.length - 1;
        this.changed();
    }

    private select(index: number) {
        if (index < 0 || index >= this.annotations.length) return;
        this.setEditingCamera(false);
        this.selectedIndex = index;
        this.gizmo.detach();
        this.gizmoEntity.setPosition(new Vec3(this.annotations[index].position));
        this.gizmo.attach([this.gizmoEntity]);
        const initial = this.annotations[index].camera.initial;
        this.events.fire('camera.setPose', {
            position: new Vec3(initial.position),
            target: new Vec3(initial.target),
            fov: initial.fov
        }, 2);
        this.changed();
    }

    private deselect() {
        if (this.selectedIndex < 0) return;
        this.setEditingCamera(false);
        this.selectedIndex = -1;
        this.gizmo.detach();
        this.changed();
    }

    private remove(index: number) {
        if (index < 0 || index >= this.annotations.length) return;
        this.setEditingCamera(false);
        this.annotations.splice(index, 1);
        this.selectedIndex = Math.min(this.selectedIndex, this.annotations.length - 1);
        if (this.selectedIndex < 0) this.gizmo.detach();
        else {
            this.gizmoEntity.setPosition(new Vec3(this.annotations[this.selectedIndex].position));
            this.gizmo.attach([this.gizmoEntity]);
        }
        this.changed();
    }

    private move(from: number, to: number) {
        if (from < 0 || from >= this.annotations.length || to < 0 || to >= this.annotations.length || from === to) return;

        const [annotation] = this.annotations.splice(from, 1);
        this.annotations.splice(to, 0, annotation);

        if (this.selectedIndex === from) {
            this.selectedIndex = to;
        } else if (from < this.selectedIndex && this.selectedIndex <= to) {
            this.selectedIndex--;
        } else if (to <= this.selectedIndex && this.selectedIndex < from) {
            this.selectedIndex++;
        }

        if (this.selectedIndex >= 0) {
            this.gizmoEntity.setPosition(new Vec3(this.annotations[this.selectedIndex].position));
        }
        this.changed();
    }

    private clear() {
        if (this.annotations.length === 0 && this.selectedIndex === -1) return;
        this.setEditingCamera(false);
        this.annotations.length = 0;
        this.selectedIndex = -1;
        this.gizmo.detach();
        this.changed();
    }

    private setEditingCamera(editing: boolean) {
        if (this.editingCamera === editing) return;
        this.editingCamera = editing;
        // Camera gestures must not accidentally move the annotation's gizmo.
        this.gizmo.detach();
        if (!editing && this.selectedIndex >= 0) {
            this.gizmoEntity.setPosition(new Vec3(this.annotations[this.selectedIndex].position));
            this.gizmo.attach([this.gizmoEntity]);
        }
        this.events.fire('annotation.cameraEditingChanged', editing);
        this.scene.forceRender = true;
    }

    private changed() {
        this.events.fire('annotations.changed', this.annotations);
        this.scene.forceRender = true;
    }
}

export { Annotations, Annotation };
